import { Button } from "@/ingot";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUpdateCartLine } from "@/api/queries";
import type { Cart, CartLine } from "@/api/types";

import { apiFetch } from "@/lib/api";
import { useDisplayMoney } from "@/lib/currency";
import { minorToMajor } from "@/lib/money";
import { formatOrderLineDimensions } from "@/lib/orderDisplay";

interface PerLineHint {
  lineId: number;
  itemFileName: string;
  next_min_qty: number;
  add_qty: number;
  next_discount_pct: number;
  current_unit_price_czk: number;
  next_unit_price_czk: number;
  /** KAN-1876 — cena/ks, kterou řádek v košíku DOOPRAVDY má (zmrazená při
   * přidání). ``current_unit_price_czk`` je živý ceník při témž počtu. */
  frozen_unit_price_czk: number;
  /** Sleva, kterou zákazník navýšením opravdu dostane: zmrazená → živá
   * cena v dalším pásmu. Bez změny ceníku = sleva pásma. */
  effective_discount_pct: number;
  /** Ceník se od přidání změnil (živá cena při témž počtu ≠ zmrazená). */
  price_list_changed: boolean;
}

/**
 * KAN-1876 — porovnání se SKUTEČNOU cenou řádku, ne s živým ceníkem.
 *
 * ``/pricing/bulk-hint`` počítá obě ceny dnešním ceníkem. Když ho obchod
 * od přidání do košíku změnil, slibovalo by „ušetříte 8 %" u kliknutí,
 * které cenu/ks zvedne ze 17 na 74 Kč (úprava řádek přecení aktuálním
 * ceníkem — rozhodnutí vlastníka 2026-10-05). Proto: sleva se počítá ze
 * zmrazené ceny řádku; když navýšení ve skutečnosti nezlevní, banner se
 * nenabídne vůbec.
 */
export function withFrozenPrice(
  hint: Omit<
    PerLineHint,
    "frozen_unit_price_czk" | "effective_discount_pct" | "price_list_changed"
  >,
  frozenUnitPrice: number | null,
): PerLineHint | null {
  const frozen = frozenUnitPrice ?? hint.current_unit_price_czk;
  const changed = Math.round(frozen) !== Math.round(hint.current_unit_price_czk);
  if (!changed) {
    return {
      ...hint,
      frozen_unit_price_czk: frozen,
      effective_discount_pct: hint.next_discount_pct,
      price_list_changed: false,
    };
  }
  if (frozen <= 0 || hint.next_unit_price_czk >= frozen) return null;
  return {
    ...hint,
    frozen_unit_price_czk: frozen,
    effective_discount_pct: (1 - hint.next_unit_price_czk / frozen) * 100,
    price_list_changed: true,
  };
}

/**
 * Single deduped bulk-discount banner shown above the cart table.
 *
 * Iter 4 user feedback: showing the per-line BulkHintRow under every
 * configuration row was repetitive — three variants of the same DXF
 * meant three identical "objednejte +N ks" boxes. The fix is one
 * banner, picking the most rewarding (= highest `next_discount_pct`)
 * line across the whole cart, with an "Apply" button that bumps that
 * line's quantity to `next_min_qty` in a single PATCH.
 *
 * Implementation:
 *  - Fetch per-line hints in parallel (one POST `/pricing/bulk-hint`
 *    per line). Hints are independent, so this is `Promise.all`.
 *  - Pick the hint with the largest `next_discount_pct`. Ties broken
 *    by smallest `add_qty` (the cheaper way to reach the tier).
 *  - "Apply" calls `useUpdateCartLine` with the new quantity. The
 *    debounced PATCH path inside `CartLineRow` would also work, but
 *    a direct mutate is more responsive for this one-shot action.
 */
export function BulkHintBanner({
  cart,
  locale,
}: {
  cart: Cart;
  locale: string;
}): JSX.Element | null {
  const { t } = useTranslation("cart");
  const { format } = useDisplayMoney(locale);
  const update = useUpdateCartLine();
  const [hints, setHints] = useState<PerLineHint[]>([]);

  // Flatten cart into lines + their parent item analysis so we can
  // build the bulk-hint request payload per line.
  const linesWithAnalysis = useMemo(
    () =>
      cart.items.flatMap((item) =>
        item.lines.map((line) => ({
          line,
          fileName: item.file_name,
          analysis: item.analysis,
          // Primary op carries the item's process; older items with an
          // implicit primary op have no operations — the BE then falls
          // back to the platform default-primary module.
          processType:
            item.operations.find((op) => op.order_index === 0)?.process_type ??
            null,
        })),
      ),
    [cart.items],
  );

  // Refetch when the line set or any quantity changes — we want the
  // banner to re-evaluate after the user bumps a qty manually.
  const dep = useMemo(
    () =>
      linesWithAnalysis
        .map(
          ({ line }) =>
            // KAN-1876 — i zmrazená cena řádku: po přecenění se banner
            // musí porovnávat s novou.
            `${line.id}:${line.material_code}:${JSON.stringify(line.dimensions ?? {})}:${line.quantity}:${line.operation_prices?.find((p) => p.is_primary)?.unit_price_minor ?? ""}`,
        )
        .join("|"),
    [linesWithAnalysis],
  );

  useEffect(() => {
    let cancelled = false;
    if (linesWithAnalysis.length === 0) {
      setHints([]);
      return;
    }
    const requests = linesWithAnalysis.map(({ line, fileName, analysis, processType }) => {
      // F2: the full analyzer snapshot IS the geometry channel — any
      // process with a formula gets a hint, not just sheet lines.
      return apiFetch<{ hint: Omit<PerLineHint, "lineId" | "itemFileName"> | null }>(
        "/api/v1/pricing/bulk-hint",
        {
          method: "POST",
          body: {
            material_code: line.material_code,
            // The line's variant identity, echoed verbatim through the
            // canonical channel — no top-level axis scalar.
            dimensions: line.dimensions ?? {},
            quantity: line.quantity,
            analysis,
            process_type: processType ?? undefined,
          },
        },
      )
        .then((r) => {
          if (!r.hint) return null;
          const primary = line.operation_prices?.find((p) => p.is_primary);
          return withFrozenPrice(
            { ...r.hint, lineId: line.id, itemFileName: fileName },
            primary ? minorToMajor(primary.unit_price_minor, cart.currency_code) : null,
          );
        })
        .catch(() => null);
    });
    Promise.all(requests).then((results) => {
      if (cancelled) return;
      setHints(
        results.filter((h): h is PerLineHint => h !== null),
      );
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);

  // Pick the hint with the largest discount; ties → smallest add_qty.
  const best = useMemo<PerLineHint | null>(() => {
    if (hints.length === 0) return null;
    return hints.reduce((acc, h) => {
      if (acc === null) return h;
      if (h.effective_discount_pct > acc.effective_discount_pct) return h;
      if (
        h.effective_discount_pct === acc.effective_discount_pct &&
        h.add_qty < acc.add_qty
      )
        return h;
      return acc;
    }, null as PerLineHint | null);
  }, [hints]);

  if (best === null) return null;

  // Find the canonical line so the banner has its config text and the
  // full PATCH payload (the BE schema requires every field).
  const targetLine: CartLine | undefined = cart.items
    .flatMap((i) => i.lines)
    .find((l) => l.id === best.lineId);
  // KAN-1027 — týž popiskovač, jaký používají objednávkové řádky
  // (KAN-926). Do té doby tu stálo holé ``formatDimensions`` bez mapy
  // popisků, takže banner zákazníkovi vypsal „color code: o · infill
  // pct: 20 · layer height mm: 0.2" — humanizované klíče z drátu.
  // Guard ``variant-labels-not-raw-keys`` na tuhle větu nedosáhne.
  const lineSummary = targetLine
    ? `${targetLine.material_display_name || targetLine.material_code} · ${formatOrderLineDimensions(
        {
          dimensions: targetLine.dimensions ?? {},
          dimension_labels: targetLine.dimension_labels,
        },
        t("bulkHintBanner.modifierLabel", { defaultValue: "Úprava" }),
      )}`
    : "";

  const onApply = () => {
    if (!targetLine) return;
    // Echo the stored dimensions (with the hint's axis value) through the
    // canonical channel — no top-level axis scalar. Strip
    // material_code/quantity from the echo: legacy rows' dimensions
    // output carries them as a fallback, and the BE prefers dimension
    // values over the top-level scalars we're deliberately setting.
    const echoedDimensions = Object.fromEntries(
      Object.entries(targetLine.dimensions ?? {}).filter(
        ([key]) => key !== "material_code" && key !== "quantity",
      ),
    );
    update.mutate({
      lineId: best.lineId,
      payload: {
        material_code: targetLine.material_code,
        quantity: best.next_min_qty,
        dimensions: echoedDimensions,
      },
    });
  };

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-ok-border bg-ok-bg px-4 py-3 text-sm text-ok"
      data-testid="cart-bulk-hint-banner"
    >
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {t("bulkHintBanner.heading", {
            addQty: best.add_qty,
            nextQty: best.next_min_qty,
            discountPct: Math.round(best.effective_discount_pct),
            defaultValue:
              "Přidejte +{{addQty}} ks (na {{nextQty}} ks) a ušetříte {{discountPct}} %.",
          })}
        </p>
        <p className="mt-0.5 text-xs text-ok/80">
          {/* KAN-1027 — cena je ZÁKLADNÍ SAZBA primární operace: bez
              jednorázové přípravy a bez navazujících kroků, protože
              množstevní sleva se týká jen jí. Bez toho pojmenování
              vypadala jako cena řádku a neodpovídala ničemu na stránce
              (změřeno „20 Kč/ks" u řádku za 134 Kč). */}
          {t("bulkHintBanner.body", {
            fileName: best.itemFileName,
            summary: lineSummary,
            currentUnit: format(best.frozen_unit_price_czk),
            nextUnit: format(best.next_unit_price_czk),
            defaultValue:
              "{{fileName}} · {{summary}} — základní sazba {{currentUnit}}/ks → {{nextUnit}}/ks",
          })}
        </p>
        {best.price_list_changed && (
          <p className="mt-0.5 text-xs text-warn" data-testid="cart-bulk-hint-price-list-changed">
            {t("bulkHintBanner.priceListChanged", {
              frozenUnit: format(best.frozen_unit_price_czk),
              liveUnit: format(best.current_unit_price_czk),
              defaultValue:
                "Ceník se od přidání změnil: {{frozenUnit}}/ks → {{liveUnit}}/ks. Po navýšení se řádek přecení aktuálním ceníkem.",
            })}
          </p>
        )}
      </div>
      <Button
        variant="primary"
        size="sm"
        onClick={onApply}
        loading={update.isPending}
        data-testid="cart-bulk-hint-apply"
      >
        {t("bulkHintBanner.apply", {
          nextQty: best.next_min_qty,
          defaultValue: "Navýšit na {{nextQty}} ks",
        })}
      </Button>
    </div>
  );
}
